import type { AuthSession, UserRole } from './types';

/**
 * Resolve the effective role for a specific org from a session.
 *
 * Pure (no React, no hooks) so it can be shared by `buildSession` and the
 * React `useCurrentRole()` hook without risking a hook/import cycle between
 * `@shared/auth` and `@shared/data/use-current-org` (#318).
 *
 * Resolution order:
 *   1. the role the user holds in `orgId`, when that org is one of their
 *      memberships;
 *   2. the role in the session's PRIMARY org (fallback when no org is selected
 *      yet, or the selected id isn't a current membership);
 *   3. the session's top-level `role` (defensive final fallback).
 *
 * Returns `null` when there is no session (unauthenticated / still loading), so
 * gates collapse to the "no role" state exactly as before.
 */
export function roleForOrg(
  session: AuthSession | null,
  orgId: string | null | undefined
): UserRole | null {
  if (!session) return null;
  const byOrg = session.roleByOrgId ?? {};
  if (orgId && byOrg[orgId]) return byOrg[orgId];
  const primaryId = session.org?.id;
  if (primaryId && byOrg[primaryId]) return byOrg[primaryId];
  return session.role ?? null;
}
